import { useNavigate } from "@tanstack/react-router";
import { GraduationCap, MapPin, School } from "lucide-react";
import useSWR from "swr";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { supabaseClient } from "@/lib/supabase";
import type { StaffPublic } from "@/services/entities/use-public-data";
import { PUBLIC_STORAGE_BUCKET } from "@/utils/constants";

export async function getSignedUrl(filePath, expiresIn = 3600) {
  const { data, error } = await supabaseClient.storage
    .from(PUBLIC_STORAGE_BUCKET)
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    console.error("Error creating signed URL:", error);
    return null;
  }

  return data.signedUrl;
}

export default function FullScreenTutorCard({ tutor }: { tutor: StaffPublic }) {
  const navigate = useNavigate();
  const { data, error } = useSWR(
    tutor?.photo_url ? `/tutors/${tutor.staff_id}/presignedImage` : null,
    () => getSignedUrl(tutor.photo_url!),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 1000 * 60 * 60, // 1 hour
      shouldRetryOnError: false,
    },
  );

  if (error) {
    console.log(`Failed to get tutor image: ${tutor.staff_id}. error: ${error}`);
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <Card className="mx-auto max-w-4xl">
        <CardHeader className="flex flex-col items-center gap-4 pb-2 md:flex-row md:items-start md:gap-8">
          <Avatar className="h-40 w-40 md:h-64 md:w-64">
            <AvatarImage
              alt={tutor.first_name ?? "Tutor Image"}
              src={data || ""}
              className="object-cover"
            />
            <AvatarFallback>
              {(tutor.first_name ?? "TTF")
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <h1 className="text-3xl font-bold">{`${tutor.first_name} ${tutor.last_name}`}</h1>
            <div className="mt-2 flex flex-col items-center gap-2 md:items-start">
              {tutor.location && (
                <div className="flex items-center text-lg text-muted-foreground">
                  <MapPin className="mr-1 h-5 w-5" />
                  {tutor.location}
                </div>
              )}
              {tutor.school_attended && (
                <div className="flex items-center text-lg text-muted-foreground">
                  <School className="mr-1 h-5 w-5" />
                  {tutor.school_attended}
                </div>
              )}
              {tutor.grade && (
                <div className="flex items-center text-lg text-muted-foreground">
                  <GraduationCap className="mr-1 h-5 w-5" />
                  {tutor.grade}
                </div>
              )}
              <Badge className="text-sm bg-blue-500 text-white dark:bg-blue-700 dark:text-gray-100">
                Remote
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-6">
          <section>
            <h2 className="mb-2 text-2xl font-semibold">About Me</h2>
            <p className="text-lg text-muted-foreground">{tutor.bio}</p>
          </section>
          {tutor.subjects && tutor.subjects.length > 0 && (
            <section>
              <h2 className="mb-2 text-2xl font-semibold">Subjects</h2>
              <div className="flex flex-wrap gap-2">
                {[...tutor.subjects]
                  .sort((a, b) => a.localeCompare(b))
                  .map((subject) => (
                    <Badge key={subject} variant="secondary" className="text-base">
                      {subject}
                    </Badge>
                  ))}
              </div>
            </section>
          )}
          <Button
            size="lg"
            className="mt-4"
            onClick={() =>
              navigate({
                to: "/contact",
                search: { staff_id: tutor.staff_id ?? "" },
              })
            }
          >
            Request this Tutor
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
